import React, { useEffect, useState, useCallback } from "react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ChatCircleTextIcon,
  ArrowsClockwiseIcon,
  BroomIcon,
  ClockCountdownIcon,
} from "@phosphor-icons/react";

const StatusBadge = ({ status, error }) => {
  const cfg = {
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    skipped: "bg-zinc-50 text-zinc-500 border-zinc-200",
  }[status] || "bg-zinc-50 text-zinc-500 border-zinc-200";
  return (
    <span title={error || ""}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cfg}`}>
      {status}
    </span>
  );
};

const SourceBadge = ({ source }) => {
  const cfg = {
    comment: { label: "COMMENT", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    dm: { label: "DM", cls: "bg-blue-50 text-blue-800 border-blue-200" },
    story_reply: { label: "STORY REPLY", cls: "bg-pink-50 text-pink-800 border-pink-200" },
    story_mention: { label: "STORY @", cls: "bg-pink-50 text-pink-800 border-pink-200" },
  }[source] || { label: source || "—", cls: "bg-zinc-50 text-zinc-700 border-zinc-200" };
  return (
    <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const MatchBadge = ({ status }) => {
  if (status === "matched") {
    return <span className="inline-flex items-center rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-800">MATCHED</span>;
  }
  if (status === "cooldown") {
    return (
      <span className="inline-flex items-center gap-1 rounded-sm border border-yellow-200 bg-yellow-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-yellow-800">
        <ClockCountdownIcon size={10} weight="bold" /> COOLDOWN
      </span>
    );
  }
  return <span className="inline-flex items-center rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-zinc-500">NO MATCH</span>;
};

export default function Activity() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.listActivity(200));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onClear = async () => {
    if (!window.confirm("Delete ALL activity? This cannot be undone.")) return;
    try {
      const res = await api.clearActivity();
      toast.success(`Deleted ${res.deleted} rows`);
      reload();
    } catch {
      toast.error("Failed to clear activity");
    }
  };

  return (
    <Layout
      title="Activity"
      subtitle="Every comment and DM Megatron has processed for your connected accounts — including cooldown skips."
      action={
        <div className="flex gap-2">
          <button onClick={onClear} data-testid="clear-activity-btn"
            className="inline-flex items-center gap-2 rounded-sm border border-zinc-200 bg-white px-6 py-2.5 text-sm font-semibold text-zinc-900 hover:bg-zinc-50">
            <BroomIcon size={16} weight="duotone" /> Clear all
          </button>
          <button onClick={reload} data-testid="refresh-activity"
            className="inline-flex items-center gap-2 rounded-sm bg-zinc-950 px-6 py-2.5 text-sm font-bold text-white hover:bg-zinc-800">
            <ArrowsClockwiseIcon size={16} weight="bold" /> Refresh
          </button>
        </div>
      }
    >
      {(() => {
        if (loading) {
          return <div className="rounded-sm border border-zinc-200 bg-white p-12 text-center text-zinc-400">Loading…</div>;
        }
        if (rows.length === 0) {
          return (
            <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-zinc-300 bg-white py-24" data-testid="activity-empty">
              <ChatCircleTextIcon size={64} weight="duotone" className="text-zinc-300" />
              <h3 className="mt-4 font-display text-2xl font-bold text-zinc-900">No activity yet</h3>
              <p className="mt-2 max-w-md text-center text-sm text-zinc-500">
                Once Instagram delivers a webhook for your connected account, you'll see every match, reply, and DM here.
              </p>
            </div>
          );
        }
        return (
          <div className="overflow-x-auto rounded-sm border border-zinc-200 bg-white" data-testid="activity-table">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50">
                <tr className="text-left">
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Source</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Time</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">User</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Comment</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Keyword</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Match</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Reply</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">DM</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const t = new Date(r.timestamp);
                  return (
                    <tr key={r.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          <SourceBadge source={r.trigger_source || "comment"} />
                          {r.backfilled && (
                            <span className="inline-flex items-center rounded-sm border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-purple-800">
                              Backfill
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 font-mono text-xs text-zinc-500">{t.toLocaleString()}</td>
                      <td className="p-4 font-mono text-xs text-zinc-700">@{r.commenter_username || r.commenter_id || "user"}</td>
                      <td className="max-w-xs truncate p-4 text-zinc-900">{r.comment_text}</td>
                      <td className="p-4">
                        {r.matched_keyword ? (
                          <span className="rounded-sm bg-amber-100 px-2 py-1 font-mono text-xs font-bold text-amber-900">
                            {r.matched_keyword}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="p-4"><MatchBadge status={r.match_status} /></td>
                      <td className="p-4"><StatusBadge status={r.public_reply_status} error={r.public_reply_error} /></td>
                      <td className="p-4"><StatusBadge status={r.dm_status} error={r.dm_error} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}
    </Layout>
  );
}
